import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigation } from '../../hooks/useNavigation';
import BackgroundShapes from '../common/BackgroundShapes';
import { userAPI } from '../../services/apiService';
import Avatar from '../common/Avatar';
import { occupations } from '../../constants/formData';
import ProfileInfoSections, { ProfileData } from './ProfileInfoSections';

interface ProfilePageProps {
  onNavigate: (page: string) => void;
}

// Format move-in date YYYY-MM-01 string to "June 2026" display label.
// Falls back to raw string for legacy enum values (e.g. "immediately").
const formatMoveInDate = (date: string | Date): string => {
  if (!date) return '';
  const d = new Date(typeof date === 'string' ? date + 'T00:00:00' : date);
  if (isNaN(d.getTime())) return typeof date === 'string' ? date : '';
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const ProfilePage: React.FC<ProfilePageProps> = ({ onNavigate }) => {
  const { user, token } = useAuth();
  const navigation = useNavigation();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  // Three-state management: undefined = loading, null = no picture, string = has picture
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch full user profile from backend
  useEffect(() => {
    const fetchProfile = async () => {
      // Don't set state to null if user/token not ready - keep as undefined (loading)
      if (!user?.id || !token) {
        setLoading(false);
        return; // Keep state as undefined (loading) - don't set to null
      }

      try {
        setLoading(true);
        setError(null);
        const response = await userAPI.getProfile(user.id, token);
        
        if (response.success && response.data?.user) {
          const userData = response.data.user;
          
          // Store profile picture URL if available
          if (userData.personalInfo?.profilePicture) {
            setProfilePictureUrl(userData.personalInfo.profilePicture); // string
          } else {
            setProfilePictureUrl(null); // No picture
          }
          
          // Map backend user data to profile display format
          // Note: occupation and languages are at root level, not nested
          const mappedData: ProfileData = {
            bio: userData.personalInfo?.bio || '',
            location: userData.housingInfo?.selectedLocations || [],
            budget: userData.housingInfo?.rentPreferences 
              ? `$${userData.housingInfo.rentPreferences.minRent}-${userData.housingInfo.rentPreferences.maxRent}`
              : 'Not specified',
            housingSituation: userData.housingInfo?.housingSituation || '',
            moveInDate: userData.housingInfo?.moveInDate 
              ? formatMoveInDate(userData.housingInfo.moveInDate)
              : '',
            occupation: userData.occupation || '', // At root level
            languages: userData.languages || [], // At root level
            cleanliness: userData.lifestyle?.cleanliness || '',
            socialLevel: userData.lifestyle?.socialLevel || '',
            workSchedule: userData.lifestyle?.workSchedule || '',
            hasPets: userData.lifestyle?.hasPets || [],
            okWithPets: userData.lifestyle?.okWithPets || []
          };
          
          setProfileData(mappedData);
        }
      } catch (err: any) {
        console.error('Error fetching profile:', err);
        setError(err instanceof Error ? err.message : 'Failed to load profile');
        setProfilePictureUrl(null); // Error, show fallback
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user, token]);

  // Navigation handlers
  const handleBackToDashboard = () => {
    try {
      navigation.goToDashboard();
    } catch {
      onNavigate('dashboard');
    }
  };

  const handleUpdateProfile = () => {
    try {
      navigation.navigate('/profile/edit');
    } catch {
      // Fallback navigation
      window.location.href = '/profile/edit';
    }
  };

  const handleImageUpload = () => {
    try {
      navigation.navigate('/profile/image');
    } catch {
      window.location.href = '/profile/image';
    }
  };


  return (
    <div className="min-h-screen bg-paper relative overflow-hidden">
      <BackgroundShapes />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold text-coffee">My Profile</h1>
            <button
              onClick={handleBackToDashboard}
              className="bg-white border-2 border-coffee text-coffee rounded-xl px-4 py-2 font-bold shadow-sm hover:bg-cream transition-colors"
            >
              ← Back to Dashboard
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border-2 border-red-400 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Main Container - The Dossier */}
          <div className="bg-cream rounded-[32px] border-4 border-coffee shadow-[8px_8px_0px_#594A42] p-8 md:p-12 relative z-10 mb-8">
            {/* Profile Header (Avatar & Name) */}
            <div className="flex flex-col items-center mb-8">
              <div className="mb-4">
                <Avatar
                  profilePictureUrl={profilePictureUrl}
                  firstName={user?.firstName}
                  lastName={user?.lastName}
                  userId={user?.id}
                  size="xlarge"
                  loading={profilePictureUrl === undefined}
                  containerClassName="border-4 border-white ring-2 ring-coffee shadow-md"
                />
              </div>
              <h2 className="text-4xl font-bold text-coffee mb-2">
                {user?.firstName} {user?.lastName}
              </h2>
              {profileData?.occupation && (
                <p className="text-xl text-coffee/70 font-medium">
                  {occupations.find(o => o.value === profileData.occupation)?.label || profileData.occupation}
                </p>
              )}
              <button
                onClick={handleImageUpload}
                className="bg-terracotta text-white border-2 border-coffee rounded-xl px-6 py-2 font-semibold hover:bg-[#d89050] transition-colors shadow-sm mt-4"
              >
                {user?.id ? 'Change Photo' : 'Upload Photo'}
              </button>
              <p className="text-coffee/70 text-xs mt-2 text-center">
                Recommended: 400x400px • Max 5MB • JPG, PNG, WebP
              </p>
            </div>

            {/* Profile Information */}
            {loading ? (
              <div className="text-center py-8">
                <p className="text-coffee/70">Loading profile...</p>
              </div>
            ) : (
              <ProfileInfoSections profileData={profileData} />
            )}

            {/* Update Profile Button */}
            <div className="mt-8">
              <button
                onClick={handleUpdateProfile}
                className="w-full bg-terracotta text-white border-2 border-coffee rounded-xl py-4 font-bold text-lg shadow-[4px_4px_0px_#594A42] hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_#594A42] transition-all"
              >
                Update Profile
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;

